import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactPixel from 'react-facebook-pixel';

/**
 * MetaPixel Component
 * 
 * Initializes and manages Meta Pixel (Facebook Pixel) tracking throughout the application.
 * Automatically tracks pageviews on route changes and respects prerender conditions.
 * 
 * Features:
 * - Initializes Meta Pixel with ID 1340573561586015
 * - Skips tracking during react-snap prerender (__IS_SNAP_RENDER flag)
 * - Tracks PageView events on route changes
 * - Includes logging for debugging and monitoring
 */
const MetaPixel = () => {
  const location = useLocation();
  const logdetails: string[] = [];

  // Initialize Meta Pixel on component mount
  useEffect(() => {
    // Skip Meta Pixel initialization during react-snap prerender
    if ((window as any).__IS_SNAP_RENDER) {
      logdetails.push('skip=prerender');
      console.log('[MetaPixel] Init skipped:', logdetails.join(' | '));
      return;
    }

    try {
      // Initialize Meta Pixel with advanced matching and auto-config disabled
      const options = {
        autoConfig: true, // Enable automatic configuration
        debug: false, // Set to true for development debugging
      };

      // Meta Pixel ID: 1340573561586015
      ReactPixel.init('1340573561586015', undefined, options);
      
      // Track initial page view
      ReactPixel.pageView();
      
      logdetails.push('status=initialized');
      logdetails.push('pixel_id=1340573561586015');
      logdetails.push('initial_pageview=tracked');
      
      console.log('[MetaPixel] Initialized:', logdetails.join(' | '));
    } catch (error) {
      logdetails.push(`error=${error}`);
      console.error('[MetaPixel] Initialization error:', logdetails.join(' | '), error);
    }
  }, []);

  // Track pageview on route change
  useEffect(() => {
    // Skip tracking during react-snap prerender
    if ((window as any).__IS_SNAP_RENDER) {
      return;
    }

    const tracklogdetails: string[] = [
      `pathname=${location.pathname}`,
      `search=${location.search || 'none'}`,
    ];

    try {
      // Track pageview for the current route
      ReactPixel.pageView();
      
      tracklogdetails.push('pageview=tracked');
      console.log('[MetaPixel] PageView:', tracklogdetails.join(' | '));
    } catch (error) {
      tracklogdetails.push(`error=${error}`);
      console.error('[MetaPixel] PageView tracking error:', tracklogdetails.join(' | '), error);
    }
  }, [location.pathname, location.search]);

  // This component doesn't render anything
  return null;
};

export default MetaPixel;
